import { useState } from "react";
import { useTranslation } from "react-i18next";

import { detectOS } from "../detect-os";
import { STEPS, type OS } from "../selfhelp-steps";

const OS_LIST: OS[] = ["ios", "android", "windows", "macos", "chromeos", "linux"];

interface SelfHelpPageProps {
  siteName: string;
  onReportProblem: () => void;
}

export function SelfHelpPage({ siteName, onReportProblem }: SelfHelpPageProps) {
  const { t } = useTranslation();
  const [os, setOs] = useState<OS>(detectOS);
  const [stepIndex, setStepIndex] = useState(0);
  const [showOsSelect, setShowOsSelect] = useState(false);
  const [solved, setSolved] = useState(false);

  const steps = STEPS[os];
  const isLastStep = stepIndex === steps.length - 1;
  const progress = ((stepIndex + 1) / steps.length) * 100;

  function handleOsChange(newOs: OS) {
    setOs(newOs);
    setShowOsSelect(false);
    setStepIndex(0);
    setSolved(false);
  }

  function handleSolved() {
    setSolved(true);
  }

  if (solved) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-md mx-auto px-4 py-8">
          <h1 className="text-xl font-semibold text-gray-800 mb-6">
            {siteName}
          </h1>
          <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
            <div className="text-green-500 text-5xl mb-3">✓</div>
            <p className="text-green-800 font-semibold text-lg">
              {t("selfhelp_solved_title")}
            </p>
            <p className="text-green-700 mt-1 text-sm">
              {t("selfhelp_solved_desc")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-md mx-auto px-4 py-8">
        <h1 className="text-xl font-semibold text-gray-800 mb-2">
          {siteName}
        </h1>
        <p className="text-gray-500 text-sm mb-6">{t("selfhelp_intro")}</p>

        {/* OS indicator and switch */}
        <div className="flex items-center gap-2 mb-4 text-sm text-gray-600">
          <span>
            {t("os_detected")}: <strong>{t(`os_${os}`)}</strong>
          </span>
          <span className="text-gray-300">|</span>
          <button
            type="button"
            className="text-blue-600 underline hover:text-blue-800"
            onClick={() => setShowOsSelect(true)}
          >
            {t("os_change")}
          </button>
        </div>

        {/* OS selection */}
        {showOsSelect && (
          <div
            role="radiogroup"
            aria-label={t("os_select_label")}
            className="flex flex-wrap gap-2 mb-6"
          >
            {OS_LIST.map((o) => (
              <button
                key={o}
                type="button"
                role="radio"
                aria-checked={os === o}
                onClick={() => handleOsChange(o)}
                className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  os === o
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                {t(`os_${o}`)}
              </button>
            ))}
          </div>
        )}

        {/* Progress */}
        <div className="mb-6">
          <p className="text-xs text-gray-500 mb-1">
            {t("step_x_of_y", {
              current: stepIndex + 1,
              total: steps.length,
            })}
          </p>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Step card */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <span className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
              {stepIndex + 1}
            </span>
            <h2 className="text-base font-semibold text-gray-800">
              {t(steps[stepIndex].titleKey)}
            </h2>
          </div>
          <p className="text-gray-600 text-sm leading-relaxed">
            {t(steps[stepIndex].descKey)}
          </p>
        </div>

        {/* Navigation */}
        <div className="flex flex-col gap-3">
          {isLastStep ? (
            <>
              <button
                type="button"
                onClick={handleSolved}
                className="w-full py-3 px-4 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-colors"
              >
                {t("step_problem_solved")}
              </button>
              <button
                type="button"
                onClick={onReportProblem}
                className="w-full py-3 px-4 bg-white border border-red-300 text-red-600 rounded-xl font-medium hover:bg-red-50 transition-colors"
              >
                {t("report_problem")}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setStepIndex((i) => i + 1)}
              className="w-full py-3 px-4 btn-primary rounded-xl font-medium transition-colors"
            >
              {t("step_next")}
            </button>
          )}
          {stepIndex > 0 && (
            <button
              type="button"
              onClick={() => setStepIndex((i) => i - 1)}
              className="w-full py-3 px-4 bg-white border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
            >
              {t("step_prev")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
